package com.swetdot.swetgram.ui.profile

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.ChatRepository
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.auth.AuthRepository
import com.swetdot.swetgram.data.media.ImageSource
import com.swetdot.swetgram.data.media.UploadPurpose
import com.swetdot.swetgram.data.media.UploadRepository
import com.swetdot.swetgram.data.profile.BlockRepository
import com.swetdot.swetgram.data.profile.PrivacyRepository
import com.swetdot.swetgram.data.profile.ProfileRepository
import com.swetdot.swetgram.domain.BlockedUser
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.domain.FieldEdit
import com.swetdot.swetgram.domain.MyProfile
import com.swetdot.swetgram.domain.PrivacySettings
import com.swetdot.swetgram.domain.ProfileQr
import com.swetdot.swetgram.domain.ScheduledDeletion
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.domain.UserProfile
import com.swetdot.swetgram.protocol.dto.ErrorCodes
import com.swetdot.swetgram.ui.auth.FieldError
import com.swetdot.swetgram.ui.auth.toAuthState
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.count
import com.swetdot.swetgram.ui.text.text
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.auth_banner_offline
import swetgram.shared.generated.resources.auth_banner_rate_limited
import swetgram.shared.generated.resources.auth_banner_rate_limited_seconds
import swetgram.shared.generated.resources.auth_banner_upgrade_required
import swetgram.shared.generated.resources.profile_error_account_gone
import swetgram.shared.generated.resources.profile_error_block
import swetgram.shared.generated.resources.profile_error_cancel_deletion
import swetgram.shared.generated.resources.profile_error_check_details
import swetgram.shared.generated.resources.profile_error_load_account
import swetgram.shared.generated.resources.profile_error_load_blocked
import swetgram.shared.generated.resources.profile_error_load_more_blocked
import swetgram.shared.generated.resources.profile_error_load_privacy
import swetgram.shared.generated.resources.profile_error_load_profile
import swetgram.shared.generated.resources.profile_error_load_public_profile
import swetgram.shared.generated.resources.profile_error_load_qr
import swetgram.shared.generated.resources.profile_error_messages_not_allowed
import swetgram.shared.generated.resources.profile_error_open_chat
import swetgram.shared.generated.resources.profile_error_prepare_edit
import swetgram.shared.generated.resources.profile_error_save_privacy
import swetgram.shared.generated.resources.profile_error_save_profile
import swetgram.shared.generated.resources.profile_error_schedule_deletion
import swetgram.shared.generated.resources.profile_error_session_expired
import swetgram.shared.generated.resources.profile_error_timeout
import swetgram.shared.generated.resources.profile_error_unblock
import swetgram.shared.generated.resources.profile_error_update_photo
import swetgram.shared.generated.resources.profile_error_username_change_too_soon
import swetgram.shared.generated.resources.profile_error_version_conflict

/**
 * The view models behind the profile surface: my profile, editing it, settings, privacy, the
 * blocked list, and somebody else's profile.
 *
 * They follow `ui/chats/ChatListViewModel.kt`: an `androidx.lifecycle.ViewModel`, work launched
 * on `viewModelScope`, state exposed as `StateFlow`, and -- the rule that matters most here --
 * a failure is reported ALONGSIDE the content rather than in place of it. Every screen in this
 * package therefore renders a banner over whatever it already had, exactly as the chat list does.
 *
 * Unlike the chat list there is no cache underneath any of this. `data/profile` is deliberately
 * cache-free (a privacy control rendered from a stale copy can tell the user their last seen is
 * hidden while the server is still publishing it), so "nothing loaded yet" is a real state each
 * of these has to carry, and `loadedOnce` is what stops an empty screen being called empty before
 * the first read settles.
 *
 * Every `catch` here takes `Throwable`, not `ApiError`. `ApiClient.execute` decodes the body
 * OUTSIDE its own try block, so a response the client cannot parse arrives as a
 * `SerializationException` and would otherwise vanish silently -- the request succeeded, nothing
 * was stored, and nothing was said. `CancellationException` is rethrown first, because swallowing
 * it detaches the coroutine from the scope that is trying to cancel it.
 */

// ---------------------------------------------------------------------------------------------
// Shared error copy
// ---------------------------------------------------------------------------------------------

/**
 * The banner for a failure, as a CHOSEN MESSAGE rather than as words.
 *
 * [fallback] is what a caller says about ITS operation ("Could not load your profile."), so the
 * generic cases stay in one place while the specific sentence stays next to the thing that
 * failed. SDS §18.2: say what happened and what to do next, never the raw server message --
 * `ERRORS.md` makes `message` an English fallback that clients are expected to replace.
 *
 * FOUR OF THE SIX BRANCHES REUSE AN `auth_banner_*` KEY, and that is the point rather than a
 * shortcut. Those sentences were already character for character identical to the ones the sign-in
 * banner shows, and a `profile_error_offline` holding a second copy of "No connection. Check your
 * network and try again." would be two spellings of one fact waiting to drift -- with the drift
 * invisible, because no reviewer has both files open. Only the two failures with no auth twin get
 * keys of their own.
 *
 * The RATE-LIMIT figure is the server's own `Long`; the plural CATEGORY needs an `Int`, so the
 * count is clamped while the number actually printed is not touched. `AuthScreens.bannerText`
 * clamps the same way and for the same reason: a `Retry-After` past `Int.MAX_VALUE` would wrap
 * negative and select a grammatical form at random.
 */
internal fun Throwable.profileMessage(fallback: UiText): UiText = when (this) {
    is ApiError.Offline -> Res.string.auth_banner_offline.text()
    is ApiError.Timeout -> Res.string.profile_error_timeout.text()
    is ApiError.UpgradeRequired -> Res.string.auth_banner_upgrade_required.text()
    is ApiError.SessionExpired -> Res.string.profile_error_session_expired.text()
    is ApiError.RateLimited -> retryAfterSeconds
        ?.let {
            Res.plurals.auth_banner_rate_limited_seconds
                .count(it.coerceIn(0L, Int.MAX_VALUE.toLong()).toInt(), it)
        }
        ?: Res.string.auth_banner_rate_limited.text()
    else -> fallback
}

/**
 * THE SAME TABLE, STILL RETURNING ENGLISH, FOR THE CALLERS THAT HAVE NOT MIGRATED.
 *
 * FOUR REMAIN, counted rather than assumed, and none of them is on the profile surface any more:
 *
 *  * `ui/groups/GroupViewModel.kt:382`   -- `groupMessage`, whose state field is a `String`.
 *  * `ui/discover/PostDetailViewModel.kt:412` -- `postDetailMessage`, likewise.
 *  * `ui/moderation/ModerationViewModel.kt:526` -- `moderationBanner`.
 *  * `ui/moderation/ReportFlow.kt:265, :466` -- the block failure and the report failure.
 *
 * The first two belong to areas this pass does not own. The moderation pair is a different
 * blockage and worth stating exactly, because "not done" and "cannot be done here" look identical
 * from outside: moderation's copy lives under the `moderation_` prefix in `values/strings.xml`,
 * which this pass does not own either, so its sentences have nowhere to land in a file it may
 * write. `ui/moderation/`'s own header records the same wait.
 *
 * So the old shape stays and the two live side by side as an OVERLOAD. A caller becomes localised
 * by changing its argument from a literal to a key, and nothing else about it moves.
 *
 * TODO(localization): delete this overload when the last of those four passes a [UiText]. It is a
 * SECOND COPY of the sentences above -- `CopyLintTest` sees both, but nothing mechanical can tell
 * that they have stopped agreeing.
 */
internal fun Throwable.profileMessage(fallback: String): String = when (this) {
    is ApiError.Offline -> "No connection. Check your network and try again."
    is ApiError.Timeout -> "The server took too long to answer."
    is ApiError.UpgradeRequired -> "This version of Swetgram is no longer supported. Please update."
    is ApiError.SessionExpired -> "Your session has ended. Please sign in again."
    is ApiError.RateLimited -> retryAfterSeconds
        ?.let { "Too many attempts. Try again in $it seconds." }
        ?: "Too many attempts. Try again shortly."
    else -> fallback
}

// ---------------------------------------------------------------------------------------------
// My profile
// ---------------------------------------------------------------------------------------------

/**
 * The QR sheet's state, kept as one object so "open but still loading" is expressible.
 *
 * Splitting it into three flows would allow `visible = false, loading = true`, which is a sheet
 * fetching something nobody is looking at.
 */
data class QrUiState(
    val visible: Boolean = false,
    val loading: Boolean = false,
    val qr: ProfileQr? = null,
    val error: UiText? = null,
)

/** SPS §13.1. The signed-in user's own profile. */
class MyProfileViewModel(
    private val profiles: ProfileRepository,
) : ViewModel() {

    /**
     * THE REPOSITORY'S COPY, NOT ONE OF ITS OWN, and that is the whole of a reported defect.
     *
     * This view model is scoped to the Profile tab's navigation entry, so it outlives every screen
     * pushed over it -- including Edit profile. While it held a private `MutableStateFlow` filled
     * once by [refresh], saving a new display name, handle, bio or photo updated
     * `EditProfileViewModel`'s copy and left this one exactly as it was read: the user came back
     * to the tab and saw the old name, with no error and nothing to retry, for as long as the
     * process lived. Reading the shared flow means the value the edit screen SAVED is the value
     * this tab renders, on the frame the save lands, with no extra request.
     */
    val profile: StateFlow<MyProfile?> = profiles.current

    private val _error = MutableStateFlow<UiText?>(null)
    val error: StateFlow<UiText?> = _error.asStateFlow()

    /** False until the first read settles, so an unloaded screen is not reported as empty. */
    private val _loadedOnce = MutableStateFlow(false)
    val loadedOnce: StateFlow<Boolean> = _loadedOnce.asStateFlow()

    private val _qr = MutableStateFlow(QrUiState())
    val qr: StateFlow<QrUiState> = _qr.asStateFlow()

    private var refreshing = false

    init {
        refresh()
    }

    fun refresh() {
        // A second tap on Retry while the first is still in flight would produce two reads and
        // two banners for one failure.
        if (refreshing) return
        refreshing = true
        viewModelScope.launch {
            try {
                // The value lands in `ProfileRepository.current`, which [profile] IS -- there is
                // no second copy here to assign it to.
                profiles.me()
                _error.value = null
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                // The previously loaded profile stays on screen; only the banner appears.
                _error.value = error.profileMessage(Res.string.profile_error_load_profile.text())
            } finally {
                refreshing = false
                _loadedOnce.value = true
            }
        }
    }

    /**
     * Opens the QR sheet and fetches the code.
     *
     * The sheet is shown BEFORE the request finishes, so the tap has a visible result on the next
     * frame rather than after a round trip. `getMyProfileQr` stops resolving while
     * `allow_profile_link` is off, which is why a failure here is phrased as a privacy setting
     * rather than as a network problem.
     */
    fun showQr() {
        if (_qr.value.loading) return
        _qr.value = QrUiState(visible = true, loading = true)
        viewModelScope.launch {
            try {
                _qr.value = QrUiState(visible = true, qr = profiles.myQr())
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _qr.value = QrUiState(
                    visible = true,
                    // Deliberately generic. The old copy blamed the profile-link switch, which
                    // cannot produce this failure -- `getMyProfileQr` serves whether the link is
                    // on or off -- so it sent the user to a setting that would not fix anything.
                    error = error.profileMessage(Res.string.profile_error_load_qr.text()),
                )
            }
        }
    }

    fun dismissQr() {
        _qr.value = QrUiState()
    }
}

// ---------------------------------------------------------------------------------------------
// Edit profile
// ---------------------------------------------------------------------------------------------

/**
 * What the edit form renders.
 *
 * [fieldErrors] is keyed by the CONTRACT's field names (`display_name`, `username`, `bio`) rather
 * than by a local enum, so a server verdict lands on the same key the client's own validation
 * uses and the two cannot disagree about which box to mark.
 */
data class EditProfileUiState(
    val profile: MyProfile? = null,
    val loadedOnce: Boolean = false,
    val saving: Boolean = false,
    val banner: UiText? = null,
    val fieldErrors: Map<String, FieldError> = emptyMap(),
    /** Flips once when the save lands. The screen navigates back and calls [EditProfileViewModel.consumeSaved]. */
    val saved: Boolean = false,
    /** An avatar is being picked, uploaded or saved. Disables the avatar control only. */
    val avatarUploading: Boolean = false,
    /** False when no image chooser is wired on this platform; the screen then offers none. */
    val canEditAvatar: Boolean = false,
) {
    /**
     * A save is only possible with a version to pin it to. `MyProfile.version` is optional in the
     * contract, and inventing one would let a stale write pass the server's check.
     */
    val canSave: Boolean get() = profile?.version != null && !saving
}

class EditProfileViewModel(
    private val profiles: ProfileRepository,
    /**
     * A LOOKUP, not a picker.
     *
     * The Android chooser belongs to the Activity that registered it, and this view model outlives
     * that Activity -- a rotation or a restore after process death replaces it, and the container
     * is handed a new picker. A captured instance would go on pointing at the destroyed one, where
     * the pick never comes back and the avatar control just stops responding. So it is fetched on
     * the tap instead; see [changeAvatar].
     *
     * Returns null where no chooser exists. Defaulted so existing constructions and tests keep
     * compiling, and so a platform without a picker degrades to a screen with no avatar control
     * rather than one whose control does nothing.
     */
    private val images: () -> ImageSource? = { null },
    private val uploads: UploadRepository? = null,
) : ViewModel() {

    // WHETHER a chooser exists is a property of the platform and does not change over this
    // screen's life, so it is safe to settle here; only the picker's identity is refetched.
    private val _state = MutableStateFlow(
        EditProfileUiState(canEditAvatar = images() != null && uploads != null),
    )
    val state: StateFlow<EditProfileUiState> = _state.asStateFlow()

    init {
        refresh()
    }

    /**
     * Picks an image, uploads it, and saves it as the avatar.
     *
     * Saved IMMEDIATELY rather than held until the form's Save button, and that is the important
     * decision here. `updateMe` is version-pinned, so holding the new media id until Save would
     * make a picked avatar one more thing that a `409 version_conflict` could throw away -- after
     * the user had already waited for the upload. Writing it now also means the presigned object
     * is attached rather than left as an orphan the user has no way to clean up.
     *
     * The uploaded media is DISCARDED if the profile write fails. Otherwise a failed save leaves
     * a paid-for object in the bucket that nothing references and nothing will ever delete.
     */
    fun changeAvatar() {
        // Read here, on the tap, so it is the CURRENT Activity's chooser -- see [images].
        val picker = images() ?: return
        val uploader = uploads ?: return
        val current = _state.value
        if (current.avatarUploading || current.saving) return
        val profile = current.profile ?: return
        val edit = profile.startEdit() ?: run {
            _state.value = current.copy(
                banner = Res.string.profile_error_prepare_edit.text(),
            )
            return
        }

        _state.value = current.copy(avatarUploading = true, banner = null)
        viewModelScope.launch {
            var uploadedId: String? = null
            try {
                val picked = picker.pick(UploadPurpose.AVATAR) ?: return@launch
                val media = uploader.upload(picked, UploadPurpose.AVATAR)
                uploadedId = media.id
                val saved = profiles.updateProfile(edit.copy(avatarMediaId = FieldEdit.Set(media.id)))
                // Cleared: the id is attached now, so the failure path below must not delete it.
                uploadedId = null
                _state.value = _state.value.copy(profile = saved)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    banner = error.profileMessage(Res.string.profile_error_update_photo.text()),
                )
                // A stale version is recovered the same way `save` recovers from one.
                refresh()
            } finally {
                // Best effort, and swallowed: reporting the cleanup's failure instead of the
                // save's would hide why the photo did not change.
                uploadedId?.let { orphan -> runCatching { uploader.discard(orphan) } }
                _state.value = _state.value.copy(avatarUploading = false)
            }
        }
    }

    /**
     * Re-reads the profile. Also the recovery from a `409 version_conflict`: the edit has to be
     * re-pinned to the revision that actually exists before it can be sent again.
     */
    fun refresh() {
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(
                    profile = profiles.me(),
                    loadedOnce = true,
                    banner = null,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    banner = error.profileMessage(Res.string.profile_error_load_profile.text()),
                )
            }
        }
    }

    /**
     * Saves the form.
     *
     * Only fields the user actually changed are sent. That is not an optimisation -- `updateMe`
     * accepts a partial body, so sending every field would turn this form into a
     * read-modify-write that clobbers a change made on another device to a field this screen was
     * never even showing.
     *
     * An emptied bio is sent as [FieldEdit.Clear], which the repository writes as an explicit
     * JSON null. Omitting it would mean "leave alone" and the old bio would come back.
     */
    fun save(
        displayName: String,
        username: String,
        bio: String,
        dateOfBirth: String,
        showBirthYear: Boolean,
        location: String,
        useLocationHint: Boolean,
    ) {
        val current = _state.value
        // Checked first, so a second tap cannot even reach the state writes below.
        if (current.saving) return
        val profile = current.profile ?: return
        val edit = profile.startEdit() ?: run {
            // No version came back, so there is nothing safe to write against.
            _state.value = current.copy(
                banner = Res.string.profile_error_prepare_edit.text(),
            )
            return
        }

        val pending = edit.copy(
            displayName = displayName.trim().takeIf { it != profile.user.displayName },
            username = username.trim().lowercase().takeIf { it != profile.user.username },
            bio = bioEdit(bio, profile.user.bio),
            dateOfBirth = textEdit(dateOfBirth, profile.user.dateOfBirth),
            showBirthYear = if (showBirthYear != profile.user.showBirthYear) {
                FieldEdit.Set(showBirthYear)
            } else {
                FieldEdit.Unchanged
            },
            location = if (useLocationHint) {
                FieldEdit.Unchanged
            } else {
                textEdit(location, profile.user.location)
            },
            useLocationHint = useLocationHint,
        )
        if (!pending.hasChanges) {
            // The contract's `minProperties: 2` makes an empty edit a guaranteed 400, and there
            // is nothing to tell the user beyond "nothing changed" -- so treat it as done.
            _state.value = current.copy(saved = true)
            return
        }

        _state.value = current.copy(saving = true, banner = null, fieldErrors = emptyMap())
        viewModelScope.launch {
            try {
                val saved = profiles.updateProfile(pending)
                _state.value = _state.value.copy(profile = saved, saving = false, saved = true)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(saving = false).withSaveFailure(error)
                // A stale version can only be recovered by reading the current one. Done here
                // rather than asked of the user, because "reload and try again" is a step they
                // should not have to understand.
                if (error is ApiError.VersionConflict) refresh()
            }
        }
    }

    fun consumeSaved() {
        _state.value = _state.value.copy(saved = false)
    }

    fun dismissBanner() {
        _state.value = _state.value.copy(banner = null)
    }
}

/**
 * Splits a save failure into "put this under a box" and "say this above the form".
 *
 * Field-level unpacking is delegated to `ApiError.toAuthState()` rather than reimplemented: it
 * already maps the contract's `validation_errors` onto [FieldError], and reusing it keeps one
 * copy table AND keeps `ValidationErrorItem` -- a wire DTO -- out of this file
 * (IMPLEMENTATION_NOTES §10). Only its `fieldErrors` are taken; its banner speaks about signing
 * in, which is the wrong sentence on a profile form.
 *
 * INTERNAL rather than private so the split can be tested. Which half of the form a failure lands
 * on is a rule -- a taken username marks the username box and says nothing above the form, a stale
 * version says everything above the form and marks no box -- and getting it wrong is silent: the
 * user sees a banner about a field they cannot find, or a red box under a field they did not
 * touch. There is no renderer on this host to notice either.
 */
internal fun EditProfileUiState.withSaveFailure(error: Throwable): EditProfileUiState = when (error) {
    is ApiError.Validation -> error.toAuthState().fieldErrors.let { mapped ->
        copy(
            fieldErrors = mapped,
            // Profile-only fields are not part of Auth's field table. Never turn such a server
            // rejection into a silent form that appears to have ignored Save.
            banner = if (mapped.isEmpty()) Res.string.profile_error_check_details.text() else null,
        )
    }
    is ApiError.VersionConflict -> copy(
        banner = Res.string.profile_error_version_conflict.text(),
    )
    is ApiError.Conflict -> when (error.code) {
        ErrorCodes.USERNAME_TAKEN, ErrorCodes.USERNAME_RESERVED ->
            copy(fieldErrors = mapOf(FIELD_USERNAME to FieldError.UsernameReserved))
        ErrorCodes.USERNAME_CHANGE_TOO_SOON ->
            copy(banner = Res.string.profile_error_username_change_too_soon.text())
        else -> copy(banner = Res.string.profile_error_save_profile.text())
    }
    else -> copy(banner = error.profileMessage(Res.string.profile_error_save_profile.text()))
}

/**
 * Blank and "no bio" are the same thing to the user, so an emptied box CLEARS rather than storing
 * an empty string -- otherwise every reader downstream has to test for `""` as well as null.
 */
private fun bioEdit(typed: String, stored: String?): FieldEdit<String> {
    return textEdit(typed, stored)
}

private fun textEdit(typed: String, stored: String?): FieldEdit<String> {
    val next = typed.trim()
    val before = stored.orEmpty().trim()
    return when {
        next == before -> FieldEdit.Unchanged
        next.isEmpty() -> FieldEdit.Clear
        else -> FieldEdit.Set(next)
    }
}

/** The contract's own field names, so a server verdict and a local check mark the same box. */
internal const val FIELD_DISPLAY_NAME = "display_name"
internal const val FIELD_USERNAME = "username"
internal const val FIELD_BIO = "bio"
internal const val FIELD_DATE_OF_BIRTH = "date_of_birth"
internal const val FIELD_LOCATION = "location"
internal const val FIELD_LOCATION_HINT = "use_location_hint"

// ---------------------------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------------------------

data class SettingsUiState(
    val profile: MyProfile? = null,
    val loadedOnce: Boolean = false,
    val busy: Boolean = false,
    val error: UiText? = null,
    /**
     * Set only by a deletion this session requested.
     *
     * The contract has no operation that READS a pending deletion -- `requestAccountDeletion`
     * returns the schedule and nothing else reports it, and `UserPrivate` carries no such field.
     * So after the app is restarted the client genuinely cannot know a deletion is pending, and
     * this screen must not pretend otherwise by showing "no deletion scheduled".
     */
    val deletion: ScheduledDeletion? = null,
)

class SettingsViewModel(
    private val profiles: ProfileRepository,
    private val auth: AuthRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(SettingsUiState())
    val state: StateFlow<SettingsUiState> = _state.asStateFlow()

    init {
        refresh()
    }

    /** The account section shows the email and its verification state, so it needs the profile. */
    fun refresh() {
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(profile = profiles.me(), loadedOnce = true, error = null)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    error = error.profileMessage(Res.string.profile_error_load_account.text()),
                )
            }
        }
    }

    /**
     * Ends the session on this device.
     *
     * `AuthRepository.logout` clears the local session even when the network call fails, so there
     * is no failure state to render -- the user ends up signed out either way, and the app's own
     * session gate moves them back to sign-in. Presence is deliberately NOT declared offline
     * first: the session is revoked here, which takes its presence with it, and a 15-second
     * timeout on a presence POST would delay the one action the user asked for.
     */
    fun signOut() {
        viewModelScope.launch { auth.logout() }
    }

    /**
     * `requestAccountDeletion`. The account survives the recovery window and the response says
     * how long it is -- displayed as sent rather than assumed, since the contract's 30 days is
     * a server policy the client should not hardcode into a promise.
     */
    fun requestDeletion() {
        if (_state.value.busy) return
        _state.value = _state.value.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(busy = false, deletion = profiles.scheduleDeletion())
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    busy = false,
                    error = error.profileMessage(Res.string.profile_error_schedule_deletion.text()),
                )
            }
        }
    }

    fun cancelDeletion() {
        if (_state.value.busy) return
        _state.value = _state.value.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                profiles.cancelDeletion()
                _state.value = _state.value.copy(busy = false, deletion = null)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                // The banner stays over the deletion card, so the account is still visibly
                // scheduled -- the one thing that must not silently look cancelled.
                _state.value = _state.value.copy(
                    busy = false,
                    error = error.profileMessage(Res.string.profile_error_cancel_deletion.text()),
                )
            }
        }
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}

// ---------------------------------------------------------------------------------------------
// Privacy
// ---------------------------------------------------------------------------------------------

/**
 * SPS §16.1.
 *
 * The contract's update body is the whole `PrivacySettings` object, so every change is a
 * REPLACEMENT. That has a consequence this view model has to handle: two changes made quickly --
 * flipping two switches -- are two full writes, and if they overlap the slower one lands last
 * and undoes the faster one. So writes are single-flight with a last-write-wins queue rather
 * than fired in parallel.
 */
class PrivacyViewModel(
    private val privacy: PrivacyRepository,
) : ViewModel() {

    private val _settings = MutableStateFlow<PrivacySettings?>(null)
    val settings: StateFlow<PrivacySettings?> = _settings.asStateFlow()

    private val _error = MutableStateFlow<UiText?>(null)
    val error: StateFlow<UiText?> = _error.asStateFlow()

    private val _saving = MutableStateFlow(false)
    val saving: StateFlow<Boolean> = _saving.asStateFlow()

    private val _loadedOnce = MutableStateFlow(false)
    val loadedOnce: StateFlow<Boolean> = _loadedOnce.asStateFlow()

    /** The last state the SERVER confirmed. What an optimistic change rolls back to. */
    private var confirmed: PrivacySettings? = null

    /** A change made while a write was already in flight. Last one wins; earlier ones are moot. */
    private var queued: PrivacySettings? = null

    init {
        refresh()
    }

    fun refresh() {
        viewModelScope.launch {
            try {
                val loaded = privacy.settings()
                confirmed = loaded
                _settings.value = loaded
                _error.value = null
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _error.value = error.profileMessage(Res.string.profile_error_load_privacy.text())
            } finally {
                _loadedOnce.value = true
            }
        }
    }

    /**
     * Applies a change optimistically, then writes it.
     *
     * Optimistic because a control that waits for a round trip before moving feels broken, and
     * rolled back on failure because a control that stays where the user put it while the server
     * still holds the old value is worse than broken -- on a privacy screen it is a lie about who
     * can see them.
     */
    fun update(next: PrivacySettings) {
        _settings.value = next
        if (_saving.value) {
            queued = next
            return
        }
        write(next)
    }

    private fun write(next: PrivacySettings) {
        _saving.value = true
        _error.value = null
        viewModelScope.launch {
            try {
                // The SERVER's copy, not the argument: the screen then shows what was actually
                // stored rather than what was asked for.
                val stored = privacy.update(next)
                confirmed = stored
                // Only adopt it if nothing newer is already pending, or the queued change would
                // visibly flip back for a frame before being written.
                if (queued == null) _settings.value = stored
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                queued = null
                _settings.value = confirmed
                _error.value = error.profileMessage(Res.string.profile_error_save_privacy.text())
            } finally {
                _saving.value = false
                queued?.let {
                    queued = null
                    write(it)
                }
            }
        }
    }

    fun dismissError() {
        _error.value = null
    }
}

// ---------------------------------------------------------------------------------------------
// Blocked users
// ---------------------------------------------------------------------------------------------

data class BlockedUsersUiState(
    val items: List<BlockedUser> = emptyList(),
    val loading: Boolean = false,
    val loadingMore: Boolean = false,
    val hasMore: Boolean = false,
    val error: UiText? = null,
    val loadedOnce: Boolean = false,
)

/** SPS §16.1's blocked list, cursor-paginated. */
class BlockedUsersViewModel(
    private val blocks: BlockRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(BlockedUsersUiState())
    val state: StateFlow<BlockedUsersUiState> = _state.asStateFlow()

    /**
     * Opaque and server-owned. Never parsed, compared or persisted -- and reset on every refresh,
     * because a cursor from the previous page set points into a list that no longer exists.
     */
    private var cursor: String? = null

    init {
        refresh()
    }

    fun refresh() {
        if (_state.value.loading) return
        _state.value = _state.value.copy(loading = true, error = null)
        viewModelScope.launch {
            try {
                val page = blocks.blockedUsers()
                cursor = page.nextCursor
                _state.value = BlockedUsersUiState(
                    items = page.items,
                    hasMore = page.hasMore,
                    loadedOnce = true,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loading = false,
                    loadedOnce = true,
                    error = error.profileMessage(Res.string.profile_error_load_blocked.text()),
                )
            }
        }
    }

    fun loadMore() {
        val current = _state.value
        // `hasMore` and a cursor are two different claims; requiring both stops a page request
        // that could only ever repeat the first page.
        if (current.loading || current.loadingMore || !current.hasMore) return
        val next = cursor ?: return

        _state.value = current.copy(loadingMore = true)
        viewModelScope.launch {
            try {
                val page = blocks.blockedUsers(cursor = next)
                cursor = page.nextCursor
                _state.value = _state.value.copy(
                    items = _state.value.items + page.items,
                    hasMore = page.hasMore,
                    loadingMore = false,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                // hasMore is left alone, so the end of the list can be retried.
                _state.value = _state.value.copy(
                    loadingMore = false,
                    error = error.profileMessage(Res.string.profile_error_load_more_blocked.text()),
                )
            }
        }
    }

    /**
     * Removes the row first and puts it back if the call fails.
     *
     * Optimistic in this direction only. The reverse -- adding a block optimistically -- would
     * show someone as blocked while they could still write, which is the failure mode a safety
     * control must never have. Removing one early is the safe direction: the worst case is a row
     * that reappears with an explanation.
     */
    fun unblock(userId: UserId) {
        val before = _state.value.items
        if (before.none { it.user.id == userId }) return

        _state.value = _state.value.copy(
            items = before.filterNot { it.user.id == userId },
            error = null,
        )
        viewModelScope.launch {
            try {
                blocks.unblock(userId)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                // Restored from the snapshot rather than by re-inserting, so the row returns to
                // its original position instead of jumping to the end.
                _state.value = _state.value.copy(
                    items = before,
                    error = error.profileMessage(Res.string.profile_error_unblock.text()),
                )
            }
        }
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}

// ---------------------------------------------------------------------------------------------
// Public profile, blocking and reporting
// ---------------------------------------------------------------------------------------------

/*
 * REPORTING USED TO LIVE HERE AND NO LONGER DOES.
 *
 * This file carried a placeholder `ReportReason`, a `ReportSubmitter` function interface, a
 * `ReportUiState` and a `REPORT_DETAILS_MAX`, all with a note saying they would move once a
 * repository existed to consume them. It does: `data/moderation/ReportRepository.kt`, with the
 * domain types in `data/moderation/Moderation.kt` and the whole flow -- reasons, confirmation,
 * network call, receipt and SPS §16.2's block offer -- in `ui/moderation/`.
 *
 * They moved rather than being duplicated because SPS §16.3 names FIVE reportable things across
 * four packages, and a per-screen report dialog is five copies of one seven-item list drifting from
 * the first day. `ui/moderation/ReportFlow.kt`'s header carries that argument in full.
 *
 * What is left here is the profile's own half: the Report BUTTON, which raises a `ReportRequest`
 * and knows nothing else.
 */

data class PublicProfileUiState(
    val profile: UserProfile? = null,
    val loadedOnce: Boolean = false,
    val error: UiText? = null,
    val qr: QrUiState = QrUiState(),
    /** A block or unblock is in flight; the action is disabled rather than tappable twice. */
    val busy: Boolean = false,
    /**
     * True while `createOrGetDirect` is in flight, so the Message button shows a spinner rather
     * than sitting inert for the length of a round trip.
     */
    val opening: Boolean = false,
    /**
     * The conversation to open, once one exists. A ONE-SHOT signal: the caller navigates and then
     * calls [PublicProfileViewModel.consumeOpened], or the next recomposition pushes the same
     * conversation onto the back stack again.
     */
    val openChatId: ChatId? = null,
)

/**
 * SPS §13.2. Somebody else's profile, keyed by username because that is what the contract's
 * `getUserByUsername` takes and what a profile link resolves to.
 *
 * NOT built: "shared groups if privacy allows". `UserPublic` carries no such field and no
 * operation returns groups in common, so there is nothing to render. The privacy scope that would
 * gate it (`show_online`/`allow_messages_from`'s `shared_groups`) still ships on the privacy
 * screen.
 */
class PublicProfileViewModel(
    private val username: String,
    private val profiles: ProfileRepository,
    private val blocks: BlockRepository,
    /**
     * Opens the direct conversation behind the Message button.
     *
     * Nullable so a preview or a test can build this screen with nothing behind it; the button is
     * then still drawn -- it is SPS §13.2's primary action and its absence would be the stranger
     * defect -- but the tap is a no-op rather than a crash. In the app it is always present.
     */
    private val chats: ChatRepository? = null,
) : ViewModel() {

    private val _state = MutableStateFlow(PublicProfileUiState())
    val state: StateFlow<PublicProfileUiState> = _state.asStateFlow()

    private var loading = false

    init {
        refresh()
    }

    fun refresh() {
        if (loading) return
        loading = true
        viewModelScope.launch {
            try {
                val profile = profiles.userByUsername(username)
                _state.value = _state.value.copy(
                    profile = profile,
                    loadedOnce = true,
                    error = null,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    error = when (error) {
                        // Enumerated by the contract, and the one failure that is not worth a
                        // retry button: the account is not there.
                        is ApiError.NotFound -> Res.string.profile_error_account_gone.text()
                        else -> error.profileMessage(
                            Res.string.profile_error_load_public_profile.text(),
                        )
                    },
                )
            } finally {
                loading = false
            }
        }
    }

    /**
     * Blocks the user, then re-reads the profile.
     *
     * NOT optimistic, unlike unblocking. The server projects a blocked user's profile down to its
     * limited form (no bio, no presence), and guessing that projection locally would mean showing
     * a state the server has not agreed to -- on the screen whose whole job is to say truthfully
     * whether this person is blocked.
     */
    fun block() {
        val target = _state.value.profile?.user?.id ?: return
        mutateBlock(Res.string.profile_error_block.text()) { blocks.block(target) }
    }

    fun unblock() {
        val target = _state.value.profile?.user?.id ?: return
        mutateBlock(Res.string.profile_error_unblock.text()) { blocks.unblock(target) }
    }

    private fun mutateBlock(failure: UiText, action: suspend () -> Unit) {
        if (_state.value.busy) return
        _state.value = _state.value.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                action()
                _state.value = _state.value.copy(busy = false)
                refresh()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    busy = false,
                    error = error.profileMessage(failure),
                )
            }
        }
    }

    /**
     * SPS §13.2's Message button: open the direct conversation with this person, or the existing
     * one.
     *
     * `createOrGetDirect` is idempotent by STATUS rather than by an idempotency key -- 201 when it
     * created the conversation, 200 when one was already there -- so a second tap cannot produce a
     * second thread, and [PublicProfileUiState.opening] exists for the user's sake rather than the
     * server's.
     *
     * The failure worth naming is `403 messages_not_allowed`: the other person's "who can message
     * me" setting is a decision the user can do nothing about, and "Could not open the chat" would
     * send them off to retry something that will never work.
     */
    fun openChat() {
        val repository = chats ?: return
        val target = _state.value.profile?.user?.id ?: return
        if (_state.value.opening) return

        _state.value = _state.value.copy(opening = true, error = null)
        viewModelScope.launch {
            try {
                val chatId = repository.openDirectChat(target.value)
                _state.value = _state.value.copy(opening = false, openChatId = chatId)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    opening = false,
                    error = when {
                        error is ApiError.Forbidden ->
                            Res.string.profile_error_messages_not_allowed.text()
                        else -> error.profileMessage(Res.string.profile_error_open_chat.text())
                    },
                )
            }
        }
    }

    fun showQr() {
        val target = _state.value.profile?.user?.id ?: return
        if (_state.value.qr.loading) return
        _state.value = _state.value.copy(qr = QrUiState(visible = true, loading = true))
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(
                    qr = QrUiState(visible = true, qr = profiles.qrFor(target)),
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    qr = QrUiState(
                        visible = true,
                        error = error.profileMessage(Res.string.profile_error_load_qr.text()),
                    ),
                )
            }
        }
    }

    fun dismissQr() {
        _state.value = _state.value.copy(qr = QrUiState())
    }

    /** Consumed by the caller BEFORE it navigates; see [PublicProfileUiState.openChatId]. */
    fun consumeOpened() {
        _state.value = _state.value.copy(openChatId = null)
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}
